import { END_POINTS } from "../constants/url";
import { AllUser } from "../types/General";
import emptySplitApi from "../utils/rtk";

type CommonResponseType = {
  statusCode: number;
  message: string;
};

type GetAllUsersResponse = {
  count: number;
  data: AllUser[];
};

type GetTokenParams = {
  limit?: number;
  page?: number;
  query?: string;
  status?: "all" | "active" | "blocked";
  plan?: string;
  fromDate?: string;
  toDate?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

type CustomerStats = {
  bankAccountsLinked?: number;
  budgetsCreated?: number;
  billsDetected?: number;
  subscriptionsDetected?: number;
};

type PlanDetails = {
  planId?: string;
  planName?: string;
  validity?: number | string;
  price?: number;
  isRecurring?: boolean;
  subscriptionEndDate?: string | null;
};

type UserProfileResponse = AllUser & {
  customerStats?: CustomerStats;
  planDetails?: PlanDetails;
  setupInfo?: {
    platform?: string;
    registrationMethod?: string;
    deviceType?: string | null;
    createdAt?: string | null;
  };
  associatedData?: {
    bankAccounts?: Array<{
      _id?: string;
      name?: string;
      institutionName?: string;
      mask?: string;
      subtype?: string;
      createdAt?: string | null;
    }>;
    budgets?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      spent?: number;
      remaining?: number;
      frequency?: string;
      createdAt?: string | null;
    }>;
    subscriptions?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      date?: string | null;
      merchantName?: string;
    }>;
    bills?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      date?: string | null;
      merchantName?: string;
    }>;
    recentTransactions?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      date?: string | null;
      merchantName?: string;
    }>;
    totals?: {
      bankAccounts?: number;
      budgets?: number;
      transactions?: number;
      subscriptionsDetected?: number;
      billsDetected?: number;
    };
  };
};
type EditUserById = {
  email: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phone: string;
};

type GetUserStatusResponse = {
  isBlocked: boolean;
};

type MarketingEmailPayload = {
  subject: string;
  message: string;
  appkey: number;
  sendToAll?: boolean;
  userIds?: string[];
  filters?: {
    query?: string;
    status?: "all" | "active" | "blocked";
    plan?: string;
    fromDate?: string;
    toDate?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  };
};
type NudgeUserPayload = {
  title?: string;
  message?: string;
  channels?: Array<"push" | "email">;
};

export const mainApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllUsers: builder.query<
      CommonResponseType & { data: GetAllUsersResponse },
      GetTokenParams
    >({
      query: ({ limit, page, query, status, plan, fromDate, toDate, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        params.set("page", String(page || 1));
        params.set("size", String(limit || 10));
        if (query?.trim()) params.set("search", query.trim());
        if (status && status !== "all") params.set("status", status);
        if (plan && plan !== "all") params.set("plan", plan);
        if (fromDate) params.set("fromDate", fromDate);
        if (toDate) params.set("toDate", toDate);
        if (sortBy) params.set("sortBy", sortBy);
        if (sortOrder) params.set("sortOrder", sortOrder);
        return {
          url: `${END_POINTS.user}?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    getUsers: builder.query<
      CommonResponseType & { data: GetAllUsersResponse },
      {}
    >({
      query: () => ({
        url: END_POINTS.user,
        method: "GET",
      }),
    }),
    usersDeleted: builder.query<
      CommonResponseType & { data: GetAllUsersResponse },
      GetTokenParams
    >({
      query: ({ limit, page, query }) => ({
        url:
          END_POINTS.usersDeleted +
          "?page=" +
          page +
          "&size=" +
          limit +
          "&search=" +
          query,
        method: "GET",
      }),
    }),
    getUserCSV: builder.query<CommonResponseType & { fileUrl: string }, {}>({
      query: () => ({
        url: END_POINTS.userCsv,
        method: "GET",
      }),
    }),
    changeUserStatus: builder.mutation<
      CommonResponseType & { data: GetUserStatusResponse },
      { body: { isBlocked: boolean }; user_id: string }
    >({
      query: ({ user_id, body }) => ({
        url: `${END_POINTS.user}/${user_id}/`,
        method: "PUT",
        body,
      }),
    }),
    getUserById: builder.query<
      CommonResponseType & { data: UserProfileResponse },
      { user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `${END_POINTS.user}/${user_id}`,
        method: "GET",
      }),
    }),

    editUserById: builder.mutation<
      CommonResponseType & { data: AllUser },
      { user_id: string; body: EditUserById }
    >({
      query: ({ user_id, body }) => ({
        url: `${END_POINTS.user}/${user_id}`,
        method: "PUT",
        body,
      }),
    }),
    deleteUserId: builder.query<
      CommonResponseType & { data: AllUser },
      { user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `${END_POINTS.user}/${user_id}`,
        method: "DELETE",
      }),
    }),
    cancelUserSubscription: builder.query<
      CommonResponseType & { data: UserProfileResponse | null },
      { user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `${END_POINTS.cancelSubscription}?userId=${user_id}`,
        method: "GET",
      }),
    }),
    sendMarketingEmail: builder.mutation<
      CommonResponseType & {
        data: {
          requestedRecipients: number;
          uniqueRecipients: number;
          sent: number;
          failed: number;
          failures: Array<{ email: string; error: string }>;
        };
      },
      { body: MarketingEmailPayload }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.user}/marketing-email`,
        method: "POST",
        body,
      }),
    }),
    nudgeUser: builder.mutation<
      CommonResponseType & {
        data: {
          userId: string;
          nudge: {
            title: string;
            message: string;
            channels: string[];
            delivery: {
              push?: { attempted?: boolean; queued?: boolean; reason?: string };
              email?: { attempted?: boolean; sent?: boolean; reason?: string };
            };
          };
        };
      },
      { user_id: string; body: NudgeUserPayload }
    >({
      query: ({ user_id, body }) => ({
        url: `${END_POINTS.user}/${user_id}/nudge`,
        method: "POST",
        body,
      }),
    }),
  }),
});
export const {
  useLazyGetAllUsersQuery,
  useLazyGetUsersQuery,
  useLazyUsersDeletedQuery,
  useLazyGetUserByIdQuery,
  useLazyGetUserCSVQuery,
  useEditUserByIdMutation,
  useChangeUserStatusMutation,
  useLazyDeleteUserIdQuery,
  useLazyCancelUserSubscriptionQuery,
  useSendMarketingEmailMutation,
  useNudgeUserMutation,
} = mainApi;
